import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { formatPrice } from '../../../utils/formatters';
import { searchService } from '../../../services/searchService';

const renderSpecValue = (val) => {
  if (val === null || val === undefined) return 'N/A';
  if (typeof val === 'boolean') return val ? 'Yes' : 'No';
  if (typeof val === 'object') {
    if (Array.isArray(val)) {
      if (val.length === 0) return 'None';
      if (val.every(item => typeof item !== 'object')) {
        return val.join(', ');
      }
      return (
        <div className="space-y-1.5 pl-2 border-l border-indigo-500/20">
          {val.map((item, idx) => (
            <div key={idx} className="text-xs bg-slate-900/40 p-1.5 rounded">
              {renderSpecValue(item)}
            </div>
          ))}
        </div>
      );
    }
    return (
      <div className="grid grid-cols-2 gap-1.5 text-xs text-slate-300">
        {Object.entries(val).map(([k, v]) => (
          <div key={k}>
            <span className="text-[10px] text-muted-foreground uppercase block font-semibold">{k.replace(/_/g, ' ')}</span>
            <span>{renderSpecValue(v)}</span>
          </div>
        ))}
      </div>
    );
  }
  return String(val);
};

const ModelInfoPanel = ({
  model,
  productSellers = [],
  onAddParentToCart,
  isVisible,
  onToggle,
  onAddToCart,
  onViewSellerReviews,
  // Component Selector Props
  components = [],
  selectedComponent,
  viewMode = 'shaded',
  onToggleZRay,
  onComponentSelect,
  onComponentDoubleClick,
  onAddComponentToCart,
  onVisibilityChange,
  componentVisibility = {}, // NEW PROP: Receive visibility from parent
  zRayComponentIds = [],
  userId = null,
  onOpenUnspecifiedPartRequest,
  isLoggedIn = false,
  onAuthRequired,
  // B2B Diagnostic CRM Props
  mode = null,
  onLogRepair,
  // 3D Wrap Configurator Props
  componentWraps = {},
  onUpdateComponentWrap,
  onRemoveComponentWrap = () => {},
  onSaveWrapConfig,
  showBeforeWrap = false,
  onToggleShowBeforeWrap,
  wrapName = 'Project Design',
  setWrapName,
  wrapNotes = '',
  setWrapNotes = () => {},
  gizmoMode = 'translate',
  onGizmoModeChange = () => {},
  gizmoSize = 0.8, // Symmetrical: Receive active gizmo size scale factor!
  onGizmoSizeChange = () => {}, // Symmetrical: Receive active gizmo size updater!
  isMultiSelectActive = false,
  onToggleMultiSelect = () => {},
  selectedComponentIds = [],
  onRemoveSelectedId = () => {},
  storeSwapConfigs = [],
  sellerComponents = [],
  configId = null // Symmetrical: Receive active Swap Config ID
  }) => {
  const navigate = useNavigate(); // Symmetrical: Instantiate single-page router navigate hook!
  const [activeTab, setActiveTab] = useState('info');
  const [savingWrap, setSavingWrap] = useState(false);
  
  const [uploadingGlbModel, setUploadingGlbModel] = useState(false);
  
  const handleGlbModelUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Symmetrical File Validations before saving
    if (file.size === 0) {
      alert('The selected 3D model file is empty.');
      return;
    }
    const maxBytes = 100 * 1024 * 1024; // 100MB limit
    if (file.size > maxBytes) {
      alert('The 3D model file exceeds the maximum allowed size of 100MB.');
      return;
    }
    if (!file.name.toLowerCase().endsWith('.glb') && !file.name.toLowerCase().endsWith('.gltf')) {
      alert('Please select a valid .glb or .gltf 3D model file.');
      return;
    }
    
    setUploadingGlbModel(true);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `swaps/${Date.now()}_${Math.random().toString(36).substring(2, 7)}.${fileExt}`;
      
      const { data, error } = await supabase.storage
        .from('product-3d-models') // Symmetrical: Use product-3d-models bucket which is database-configured to allow .glb / .gltf uploads!
        .upload(fileName, file, { 
          cacheControl: '3600', 
          upsert: true,
          contentType: 'model/gltf-binary' // Symmetrical: Explicitly specify correct GLB MIME-type to prevent application/octet-stream errors!
        });
        
      if (error) throw error;
      
      const { data: publicUrlData } = supabase.storage
        .from('product-3d-models') // Symmetrical: Retrieve public URL from the correct 3D models bucket!
        .getPublicUrl(fileName);
        
      const publicUrl = publicUrlData?.publicUrl || '';
      
      const wrapObj = componentWraps[selectedComponent?.id] || componentWraps[selectedComponent?.name] || {};
      const currentOptions = wrapObj?.swapOptions || [];
      const newOptionName = file.name.split('.')[0] || 'Uploaded Variant';
      
      const newOption = {
        glbUrl: publicUrl,
        name: newOptionName
      };

      // Update component wrap with the newly uploaded model URL and append to swapOptions variant options array!
      onUpdateComponentWrap(selectedComponent.id, { 
        subbedGlbUrl: publicUrl, // Fallback compatibility
        swapOptions: [...currentOptions, newOption],
        activeOptionIndex: currentOptions.length, // Symmetrically preview the uploaded variant instantly!
        position: wrapObj?.position || [0, 0, 0],
        rotation: wrapObj?.rotation || [0, 0, 0],
        scale: wrapObj?.scale || [1, 1, 1]
      });

      // Symmetrical Auto-Catalog Offering Integration:
      // Automatically register this newly uploaded custom GLB model directly in their storefront offerings!
      try {
        const { data: sellerAcc } = await supabase
          .from('seller_accounts')
          .select('id')
          .eq('user_id', user.id)
          .maybeSingle();
          
        if (sellerAcc) {
          const compName = file.name.split('.')[0]?.replace(/[^a-zA-Z0-9 ]/g, ' ') || 'Accessory Upgrade';
          const capitalizedName = compName.charAt(0).toUpperCase() + compName.slice(1);
          
          // 1. Insert base components entry with current vehicle product ID association!
          const { data: baseComp, error: baseErr } = await supabase
            .from('components')
            .insert({
              product_id: model?.id, // Symmetrical: Associate with current catalog vehicle product ID!
              name: capitalizedName,
              stl_file_path: publicUrl, // Map model path!
              price: 199.99,
              description: 'Custom premium 3D visual swap component uploaded and aligned via Swap Studio.'
            })
            .select()
            .single();
            
          if (baseComp) {
            // 2. Insert seller components storefront offering entry with the active Swap config relationship!
            const { error: sCompErr } = await supabase
              .from('seller_components')
              .insert({
                seller_id: sellerAcc.id,
                component_id: baseComp.id,
                custom_name: capitalizedName,
                price: 199.99,
                stock_quantity: 10,
                images_url: [publicUrl],
                swap_config_id: configId // Symmetrical: Store the associated active 3D Swap Studio project configuration ID!
              });
              
            if (sCompErr) {
              console.error('❌ Failed to create seller component offering:', sCompErr);
              throw sCompErr;
            }
            console.log('🎯 Symmetrical catalog sync success! Swapped component added to your storefront offerings.');
          } else if (baseErr) {
            console.error('❌ Failed to create base component entry:', baseErr);
            throw baseErr;
          }
        }
      } catch (catErr) {
        console.warn('Silent fallback: Failed to auto-catalog custom component:', catErr);
      }
      
      alert('3D model uploaded, aligned, and registered in your storefront catalog successfully!');
    } catch (err) {
      console.error('Failed to upload GLB model:', err);
      alert('Upload failed: ' + err.message);
    } finally {
      setUploadingGlbModel(false);
    }
  };

  const handleDecalUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const timestamp = Date.now();
      const sanitizedName = file.name.replace(/[^a-zA-Z0-9.]/g, '_');
      const fileName = `custom-decals/${timestamp}_${sanitizedName}`;

      const { error } = await supabase.storage
        .from('product-thumbnails')
        .upload(fileName, file);

      if (error) throw error;

      const { data: urlData } = supabase.storage
        .from('product-thumbnails')
        .getPublicUrl(fileName);

      if (urlData?.publicUrl) {
        onUpdateComponentWrap(selectedComponent.id, { decalUrl: urlData.publicUrl });
        alert('Custom decal uploaded and applied live successfully!');
      }
    } catch (err) {
      console.error('Error uploading decal:', err);
      alert('Failed to upload decal: ' + err.message);
    }
  };

  const handleSaveWrap = async () => {
    try {
      setSavingWrap(true);
      await onSaveWrapConfig(wrapName);
    } catch (err) {
      console.error('Error saving wrap config:', err);
      alert('Failed to save wrap configuration: ' + err.message);
    } finally {
      setSavingWrap(false);
    }
  };

  const handleAddCustomAttachment = () => {
    const nextId = `attachment-${Date.now()}`;
    const nextName = `Custom Attachment ${Object.keys(componentWraps).filter(k => k.startsWith('attachment-')).length + 1}`;

    onUpdateComponentWrap(nextId, {
      hidden: false,
      componentName: nextName,
      subbedGlbUrl: null,
      position: [0, 1.2, 0], // Spawn it on the roof of standard vehicles by default!
      rotation: [0, 0, 0],
      scale: [1, 1, 1],
      color: null
    });

    // Auto-focus selection onto this newly created attachment so they can configure it immediately!
    onComponentSelect({ id: nextId, name: nextName });

    // Symmetrical Auto-Expand: Reveal the spawned accessory inside the bottom drawer layout instantly!
    setExpandedGroups(prev => {
      const newSet = new Set(prev);
      newSet.add('Accessories & Clones');
      return newSet;
    });
    setIsComponentsExpanded(true);
  };

  // Symmetrical Custom Duplication / Clone Spawner:
  // Duplicates the currently selected swapped component wrap and offsets its position
  // to prevent overlapping z-fight visual conflicts!
  const handleCloneComponent = () => {
    if (!selectedComponent) return;
    const currentWrap = componentWraps[selectedComponent.id] || componentWraps[selectedComponent.name] || {};

    const cloneId = `attachment-${Date.now()}`;
    const cloneName = `${currentWrap?.componentName || selectedComponent.name || selectedComponent.id} (Clone)`;

    const currentPos = currentWrap.position || [0, 1.2, 0];
    const offsetPos = [currentPos[0] + 0.3, currentPos[1], currentPos[2] + 0.3]; // Offset slightly on X & Z axes!

    onUpdateComponentWrap(cloneId, {
      ...currentWrap,
      hidden: false,
      componentName: cloneName,
      position: offsetPos
    });

    // Auto-select the clone immediately!
    onComponentSelect({ id: cloneId, name: cloneName });

    // Symmetrical Auto-Expand: Reveal the spawned cloned accessory inside bottom drawer layout instantly!
    setExpandedGroups(prev => {
      const newSet = new Set(prev);
      newSet.add('Accessories & Clones');
      return newSet;
    });
    setIsComponentsExpanded(true);
  };

  const startingPrice = useMemo(() => {
    // Filter to only include active sellers that actually offer the whole product with a valid non-zero price!
    const wholeProductSellers = (productSellers || []).filter(s => {
      const priceVal = parseFloat(s.price || 0);
      return priceVal > 0 && (s.sell_product_only === true || s.sell_product_only === null);
    });

    if (wholeProductSellers.length > 0) {
      return Math.min(...wholeProductSellers.map(s => parseFloat(s.price || 0)));
    }
    return parseFloat(model?.price || 0);
  }, [productSellers, model?.price]);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedGroups, setExpandedGroups] = useState(new Set(['Hidden / Custom Swapped Parts', 'main', 'base', 'arm', 'gripper']));
  const [isComponentsExpanded, setIsComponentsExpanded] = useState(false);
  const searchTimeoutRef = useRef(null);

  const filteredComponents = useMemo(() => components?.filter(component => {
    if (!searchTerm?.trim()) return true;

    // Symmetrical Semicolon Separated Multi-Term Search:
    // Splits the search query by ';' so power users can search for "hood;rim;spoiler" simultaneously!
    const terms = searchTerm.split(';').map(t => t.trim().toLowerCase()).filter(Boolean);
    if (terms.length === 0) return true;

    return terms.some(term => {
      if (component?.name?.toLowerCase()?.includes(term)) return true;
      if (component?.group?.toLowerCase()?.includes(term)) return true;
      if (component?.description?.toLowerCase()?.includes(term)) return true;
      if (component?.customName?.toLowerCase()?.includes(term)) return true;

      // Check seller offerings / options
      const hasMatchingOption = component?.availableOptions?.some(opt => 
        opt?.name?.toLowerCase()?.includes(term) ||
        opt?.customName?.toLowerCase()?.includes(term) ||
        opt?.sellerBusinessName?.toLowerCase()?.includes(term)
      );

      return !!hasMatchingOption;
    });
  }), [components, searchTerm]);

  // Debounced search recording
  useEffect(() => {
    if (searchTerm && searchTerm.trim().length > 2) {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
      
      searchTimeoutRef.current = setTimeout(() => {
        searchService.recordSearch({
          searchTerm: searchTerm,
          category: 'viewer',
          resultsCount: filteredComponents?.length || 0,
          userId: userId
        });
      }, 1000);
    }
    
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [searchTerm, filteredComponents, userId]);

  React.useEffect(() => {
    if (selectedComponent) {
      setIsComponentsExpanded(true);
      if (selectedComponent.group) {
        setExpandedGroups(prev => {
          const newSet = new Set(prev);
          newSet.add(selectedComponent.group);
          return newSet;
        });
      }
      
      // Auto-scroll to selected component
      setTimeout(() => {
        const el = document.getElementById(`panel-comp-${selectedComponent.id}`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      }, 100);
    }
  }, [selectedComponent]);

  const tabs = [
    { id: 'info', label: 'Info', icon: 'Info' },
    { id: 'specs', label: 'Specs', icon: 'FileText' },
    { id: 'components', label: 'Parts', icon: 'Layers', count: components?.length }
  ];
  if (storeSwapConfigs && storeSwapConfigs.length > 0) {
    tabs.splice(2, 0, { id: 'swaps', label: 'Swaps', icon: 'RefreshCw', count: storeSwapConfigs.length });
  }

  const isolatedComponentId = React.useMemo(() => {
    const visibleIds = components
      .filter(c => componentVisibility[c.id] ?? true)
      .map(c => c.id);
    return visibleIds.length === 1 ? visibleIds[0] : null;
  }, [components, componentVisibility]);

  if (!isVisible) return null;

  // Component Logic
  const handleResetVisibility = () => {
    const visibilityMap = {};
    components.forEach(comp => {
      visibilityMap[comp.id] = true;
    });
    if (onVisibilityChange) {
      onVisibilityChange(visibilityMap);
    }
  };

  const groupedComponents = filteredComponents?.reduce((groups, component) => {
    const wrapObj = componentWraps[component.name] || componentWraps[component.id] || {};
    const isHiddenOrSwapped = wrapObj.hidden === true || !!wrapObj.subbedGlbUrl;
    const groupName = isHiddenOrSwapped ? 'Hidden / Custom Swapped Parts' : (component?.group || 'Other');

    if (!groups[groupName]) {
      groups[groupName] = [];
    }
    groups[groupName].push(component);
    return groups;
  }, {});

  // Symmetrical Accessories List Cloner: 
  // Reads custom spawned attachments and cloned components from state and maps them
  // cleanly into a dedicated "Accessories & Clones" group inside the Parts list drawer!
  if (groupedComponents) {
    Object.entries(componentWraps).forEach(([compId, wrap]) => {
      // Symmetrical filter: Check if key represents a custom accessory/attachment
      const isCustomAccessory = compId.startsWith('attachment-') || (wrap && typeof wrap === 'object' && !Array.isArray(wrap) && (wrap.isAccessory || wrap.is_accessory));
      if (isCustomAccessory && wrap) {
        const groupName = 'Accessories & Clones';
        if (!groupedComponents[groupName]) {
          groupedComponents[groupName] = [];
        }
        if (!groupedComponents[groupName].some(c => c.id === compId)) {
          groupedComponents[groupName].push({
            id: compId,
            name: wrap.componentName || compId,
            group: groupName,
            isCustomAccessory: true
          });
        }
      }
    });
  }

  const toggleGroup = (groupName) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded?.has(groupName)) {
      newExpanded?.delete(groupName);
    } else {
      newExpanded?.add(groupName);
    }
    setExpandedGroups(newExpanded);
  };

  const handleTabClick = (tabId) => {
    if (tabId === 'components') {
      setIsComponentsExpanded(!isComponentsExpanded);
    } else {
      setActiveTab(tabId);
    }
  };

  const handleToggleVisibility = (componentId, e) => {
    e?.stopPropagation();
    if (!isLoggedIn) {
      if (onAuthRequired) onAuthRequired();
      return;
    }
    
    if (mode === 'swap' || mode === 'wrap') {
      const wrapObj = componentWraps[componentId] || {};
      const nextHidden = !wrapObj.hidden;
      
      const compObj = components.find(c => c.id === componentId || c.dbComponentId === componentId);
      
      if (!nextHidden) {
        // Un-hiding / Restoring: Discard custom GLB replacements and custom colors completely!
        onUpdateComponentWrap(componentId, { 
          hidden: false,
          subbedGlbUrl: null,
          componentName: null,
          position: [0, 0, 0],
          rotation: [0, 0, 0],
          scale: [1, 1, 1],
          color: null
        });
        
        // Symmetrical Selection Focus Restoration:
        if (compObj && onComponentSelect) {
          onComponentSelect(compObj);
          
          // Auto-expand its target group category so they can see where it is placed!
          if (compObj.group) {
            setExpandedGroups(prev => {
              const s = new Set(prev);
              s.add(compObj.group);
              return s;
            });
          }
        }
      } else {
        // Hiding: Toggle hidden state to true
        onUpdateComponentWrap(componentId, { hidden: true });
      }
      return;
    }
    
    const compObj = components?.find(c => c.id === componentId || c.name === componentId);
    const currentVisibility = isComponentVisible(compObj || componentId);
    const newVisibility = !currentVisibility;
    
    const newMap = { ...componentVisibility };
    if (compObj) {
      if (compObj.id) newMap[compObj.id] = newVisibility;
      if (compObj.name) {
        newMap[compObj.name] = newVisibility;
        newMap[compObj.name.toLowerCase()] = newVisibility;
        const cln = cleanMeshName(compObj.name);
        if (cln) newMap[cln] = newVisibility;
      }
      if (compObj.customName) {
        newMap[compObj.customName] = newVisibility;
        const clnCustom = cleanMeshName(compObj.customName);
        if (clnCustom) newMap[clnCustom] = newVisibility;
      }
      if (compObj.dbComponentId) newMap[compObj.dbComponentId] = newVisibility;
      if (compObj.globalComponentId) newMap[compObj.globalComponentId] = newVisibility;
      if (compObj.meshUuid) newMap[compObj.meshUuid] = newVisibility;
    } else {
      newMap[componentId] = newVisibility;
    }
    
    if (onVisibilityChange) {
      onVisibilityChange(newMap);
    }
  };

  const cleanMeshName = (str) => {
    if (!str) return '';
    return str.replace(/[^a-zA-Z0-9]/g, '').toLowerCase().replace(/\d+$/, '');
  };

  const handleIsolateComponent = (componentId, e) => {
    e?.stopPropagation();
    const targetComp = components?.find(c => c.id === componentId || c.name === componentId);
    
    // Check if currently isolated: Only this component is visible, and others are hidden
    const isIsolated = components.every(comp => {
      const isVisible = isComponentVisible(comp);
      return comp.id === componentId ? isVisible : !isVisible;
    });

    const newVisibility = {};
    
    if (isIsolated) {
        // Toggle OFF: Show all components
        components.forEach(comp => {
            newVisibility[comp.id] = true;
            if (comp.name) {
              newVisibility[comp.name] = true;
              newVisibility[comp.name.toLowerCase()] = true;
              const cln = cleanMeshName(comp.name);
              if (cln) newVisibility[cln] = true;
            }
            if (comp.dbComponentId) newVisibility[comp.dbComponentId] = true;
            if (comp.meshUuid) newVisibility[comp.meshUuid] = true;
        });
    } else {
        // Toggle ON: Isolate this component
        components.forEach(comp => {
            const isTarget = comp.id === componentId;
            newVisibility[comp.id] = isTarget;
            if (comp.name) {
              newVisibility[comp.name] = isTarget;
              newVisibility[comp.name.toLowerCase()] = isTarget;
              const cln = cleanMeshName(comp.name);
              if (cln) newVisibility[cln] = isTarget;
            }
            if (comp.dbComponentId) newVisibility[comp.dbComponentId] = isTarget;
            if (comp.meshUuid) newVisibility[comp.meshUuid] = isTarget;
        });
    }

    if (onVisibilityChange) {
      onVisibilityChange(newVisibility);
    }
  };

  const isComponentVisible = (comp) => {
    if (!comp) return true;
    const compId = typeof comp === 'object' ? comp.id : comp;
    const compName = typeof comp === 'object' ? comp.name : null;
    if (componentVisibility[compId] === false) return false;
    if (compName) {
      if (componentVisibility[compName] === false || componentVisibility[compName.toLowerCase()] === false) return false;
      const cln = cleanMeshName(compName);
      if (cln && componentVisibility[cln] === false) return false;
    }
    return true;
  };

  const renderStarRating = (rating, size = 12) => {
    const stars = [];
    const fullStars = Math.floor(rating || 0);
    for (let i = 0; i < 5; i++) {
      stars.push(
        <Icon 
          key={i} 
          name="Star" 
          size={size} 
          className={i < fullStars ? "text-yellow-400 fill-current" : "text-gray-400"} 
        />
      );
    }
    return <div className="flex items-center space-x-0.5">{stars}</div>;
  };

  return (
    <div className="absolute top-4 left-4 z-30 w-[330px] bg-white/[0.88] backdrop-blur-md rounded-lg border border-border shadow-floating max-h-[calc(100vh-9rem)] overflow-hidden flex flex-col transition-all duration-200">
      {/* Symmetrical B2B Shop Return Subheader */}
      {(mode === 'repair' || mode === 'select' || mode === 'wrap' || mode === 'swap') && (
        <div className="px-4 py-2.5 border-b border-border bg-primary/5 flex items-center justify-between">
          <button
            onClick={() => {
              if (window.opener) {
                window.close();
              } else {
                navigate(mode === 'swap' ? '/shop-dashboard?tab=swap' : '/shop-dashboard'); // Symmetrical: Use single-page router navigate to prevent 403 locks!
              }
            }}
            className="text-xs font-bold text-primary hover:text-primary/80 flex items-center space-x-1 cursor-pointer"
          >
            <Icon name="ArrowLeft" size={14} />
            <span>Dashboard</span>
          </button>
          <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
            {mode === 'select' ? 'B2B Selector Mode' : mode === 'wrap' ? '3D Wrap Shop' : mode === 'swap' ? '3D Swap Studio' : 'B2B Diagnostic Mode'}
          </span>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <h3 className="text-lg font-semibold text-foreground break-words whitespace-normal leading-tight pr-2">{model?.name}</h3>
        <Button variant="ghost" size="sm" iconName="X" onClick={onToggle} className="w-8 h-8" />
      </div>

      {/* Tabs Row - Now includes Components toggle */}
      <div className="flex border-b border-border">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => handleTabClick(tab.id)}
            className={`flex-1 flex items-center justify-center space-x-1.5 py-3 text-sm font-medium transition-smooth ${
              (tab.id === 'components' && isComponentsExpanded) || (activeTab === tab.id && tab.id !== 'components')
                ? 'text-primary border-b-2 border-primary bg-muted/50' 
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Icon name={tab.icon} size={14} />
            <span className="truncate">{tab.label}</span>
            {tab.count !== undefined && (
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                isComponentsExpanded ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
              }`}>
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Content Area */}
      <div className="flex-1 overflow-y-auto min-h-0">
        <div className="p-4">
          {model?.sell_product_only && (
            <div className="bg-primary/5 border border-primary/20 p-4 rounded-xl mb-4 text-left shadow-sm">
              <div className="flex flex-col gap-3">
                <div>
                  <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider block">Product Price</span>
                  <span className="text-2xl font-black text-primary block leading-none mt-1">
                    {productSellers.length > 1 ? `From ${formatPrice(startingPrice)}` : formatPrice(startingPrice)}
                  </span>
                  {productSellers.length > 1 && (
                    <span className="text-[10px] text-muted-foreground block mt-1 font-semibold">{productSellers.length} sellers available</span>
                  )}
                </div>
                <Button
                  type="button"
                  variant="default"
                  className="bg-primary hover:bg-primary/90 font-black text-xs uppercase tracking-wider h-11 w-full shadow"
                  onClick={onAddParentToCart}
                >
                  ADD TO CART
                </Button>
              </div>
            </div>
          )}

          {activeTab === 'info' && (
            <div className="space-y-4">
              {/* Symmetrical B2B Repair Log Action CTA */}
              {selectedComponent && mode === 'repair' && (
                <div className="bg-primary/5 border border-primary/25 rounded-xl p-3 flex flex-col gap-2 shadow-sm animate-in slide-in-from-top-1 duration-200 text-left">
                  <div className="text-[10px] font-black text-primary uppercase tracking-wider">
                    🛠️ B2B Diagnostics Active
                  </div>
                  <h4 className="text-xs font-bold text-foreground leading-snug">
                    Selected: {selectedComponent.name}
                  </h4>
                  <Button
                    type="button"
                    variant="default"
                    className="bg-primary hover:bg-primary/95 text-xs font-black uppercase tracking-wider h-10 w-full"
                    onClick={() => onLogRepair && onLogRepair(selectedComponent)}
                  >
                    Log Service / Repair
                  </Button>
                </div>
              )}

              {/* Symmetrical B2B Selector Action CTA */}
              {selectedComponent && mode === 'select' && (
                <div className="bg-primary/5 border border-primary/25 rounded-xl p-3 flex flex-col gap-2 shadow-sm animate-in slide-in-from-top-1 duration-200 text-left">
                  <div className="text-[10px] font-black text-primary uppercase tracking-wider">
                    🎯 Interactive 3D Selector Active
                  </div>
                  <h4 className="text-xs font-bold text-foreground leading-snug">
                    Part: {selectedComponent.name}
                  </h4>
                  <Button
                    type="button"
                    variant="default"
                    className="bg-primary hover:bg-primary/95 text-xs font-black uppercase tracking-wider h-10 w-full"
                    onClick={() => {
                      if (window.opener) {
                        window.opener.postMessage({ type: 'part-selected', name: selectedComponent.name }, '*');
                        window.close();
                      } else {
                        alert('Parent CRM window was closed. Copied part name to clipboard: ' + selectedComponent.name);
                        navigator.clipboard.writeText(selectedComponent.name);
                      }
                    }}
                  >
                    Confirm Selection
                  </Button>
                </div>
              )}
              {/* Symmetrical B2C/B2B 3D Wrap & Swap custom configuration controls */}
              {(mode === 'wrap' || mode === 'swap') && (
                <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 flex flex-col gap-4 text-left mb-4 shadow-inner">
                  <div className="flex justify-between items-center pb-2 border-b border-slate-800/40">
                    <div className="text-[10px] font-black text-indigo-400 uppercase tracking-widest flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full bg-indigo-400 animate-pulse" />
                      {mode === 'swap' ? '3D Swap Shop Active' : '3D Wrap Shop Active'}
                    </div>
                    
                    {/* Before/After saved view toggle button */}
                    <button
                      type="button"
                      onClick={() => onToggleShowBeforeWrap(!showBeforeWrap)}
                      className={`h-7 px-3 rounded-lg text-[9px] font-black uppercase tracking-wider transition-all border flex items-center justify-center gap-1.5 shadow-sm cursor-pointer ${
                        showBeforeWrap 
                          ? 'bg-amber-100 border-amber-300 text-amber-700 hover:bg-amber-200' 
                          : 'bg-indigo-600 border-indigo-700 text-white hover:bg-indigo-500'
                      }`}
                      title={showBeforeWrap ? (mode === 'swap' ? "Switch to Custom (Swaps)" : "Switch to After View (Wrapped)") : (mode === 'swap' ? "Switch to Original Vehicle" : "Switch to Before View (Unwrapped)")}
                    >
                      <Icon name={showBeforeWrap ? "Eye" : "EyeOff"} size={10} />
                      <span>{showBeforeWrap ? (mode === 'swap' ? 'Original' : 'Before (Orig)') : (mode === 'swap' ? 'Custom (Swaps)' : 'After (Wrapped)')}</span>
                    </button>
                  </div>
                  
                  {/* Multi-Select (Bulk Color) toggle switch */}
                  <div className="flex items-center justify-between bg-slate-950/40 p-2.5 rounded-xl border border-slate-800/40">
                    <label className="text-[10px] font-black uppercase text-slate-400 cursor-pointer select-none flex items-center gap-1.5">
                      <input 
                        type="checkbox" 
                        checked={isMultiSelectActive}
                        onChange={(e) => onToggleMultiSelect(e.target.checked)}
                        className="rounded border-slate-800 text-indigo-600 focus:ring-indigo-500 h-3.5 w-3.5"
                      />
                      Enable Multi-Select (Bulk Color)
                    </label>
                    {selectedComponentIds.length > 0 && (
                      <span className="text-[9px] bg-indigo-500/20 text-indigo-400 font-extrabold px-1.5 py-0.5 rounded-full uppercase">
                        {selectedComponentIds.length} Selected
                      </span>
                    )}
                  </div>
                  
                  {selectedComponent ? (() => {
                    const selectedWrap = componentWraps[selectedComponent.name] || componentWraps[selectedComponent.id] || {};
                    return (
                      <div className="space-y-4">
                        <div className="border-b border-slate-800 pb-2">
                          <div className="flex justify-between items-center">
                            <h4 className="text-xs font-black text-slate-200 uppercase truncate max-w-[70%]">
                              Mapping: {isMultiSelectActive && selectedComponentIds.length > 1 
                                ? `${selectedComponentIds.length} Panels (Bulk)` 
                                : (selectedWrap?.componentName || selectedComponent.name)}
                            </h4>
                            <button
                              type="button"
                              onClick={() => onComponentSelect(null)}
                              className="text-[9px] font-black uppercase text-indigo-400 hover:text-indigo-300 cursor-pointer shrink-0"
                            >
                              Back to Layers
                            </button>
                          </div>
                          
                          {/* Symmetrical Selection Pill Badges */}
                          {isMultiSelectActive && selectedComponentIds.length > 1 && (
                            <div className="flex flex-wrap gap-1 mt-1.5 max-h-16 overflow-y-auto pt-1">
                              {selectedComponentIds.map(id => {
                                const compObj = components.find(c => c.id === id || c.dbComponentId === id);
                                const displayName = compObj?.name || id;
                                return (
                                  <span key={id} className="text-[9px] font-bold text-slate-300 bg-slate-950 border border-slate-800/80 px-2 py-0.5 rounded flex items-center gap-1">
                                    • {displayName}
                                    <button
                                      type="button"
                                      onClick={() => onRemoveSelectedId(id)}
                                      className="text-slate-500 hover:text-red-500 text-[8px] font-black uppercase"
                                      title={`Deselect ${displayName}`}
                                    >
                                      ×
                                    </button>
                                  </span>
                                );
                              })}
                            </div>
                          )}
                        </div>

                        {/* Symmetrical Conditional Panel Option Renderers (Wrap options vs Swap options!) */}
                        {mode === 'swap' ? (
                          <div className="space-y-4">
                            {/* 1. Hide / Show Base Part Toggle Button */}
                            <div>
                              <label className="block text-[10px] font-bold uppercase text-slate-400 mb-1.5">
                                Original Base Component Visibility
                              </label>
                              <Button
                                type="button"
                                onClick={() => onUpdateComponentWrap(selectedComponent.id, { hidden: !selectedWrap?.hidden })}
                                className={`w-full text-xs font-black uppercase h-9 border flex items-center justify-center gap-1.5 ${
                                  selectedWrap?.hidden 
                                    ? 'bg-red-600 border-red-700 hover:bg-red-500 text-white' 
                                    : 'bg-slate-950 border-slate-800 text-slate-300 hover:bg-slate-900'
                                }`}
                              >
                                <Icon name={selectedWrap?.hidden ? "EyeOff" : "Eye"} size={13} />
                                {selectedWrap?.hidden ? 'Base Component is Hidden' : 'Base Component is Visible'}
                              </Button>
                            </div>

                            {/* 2. Custom Substitute GLB Multiple Option Configurator */}
                            <div className="space-y-3.5">
                              <div>
                                <label className="block text-[10px] font-bold uppercase text-slate-400 mb-1.5">
                                  Configurable Aftermarket Options
                                </label>

                                {/* Multi-Option List Display */}
                                {selectedWrap?.swapOptions && selectedWrap.swapOptions.length > 0 ? (
                                  <div className="space-y-1.5 mb-2.5 max-h-40 overflow-y-auto pr-1">
                                    {selectedWrap.swapOptions.map((opt, idx) => {
                                      const isOptionActive = (selectedWrap.activeOptionIndex ?? 0) === idx;
                                      return (
                                        <div 
                                          key={idx} 
                                          className={`flex items-center justify-between p-2 rounded-lg border text-xs transition-colors ${
                                            isOptionActive 
                                              ? 'bg-indigo-950/30 border-indigo-500/30 text-white' 
                                              : 'bg-slate-950 border-slate-900 text-slate-400'
                                          }`}
                                        >
                                          <div className="flex items-center gap-2 truncate">
                                            <input 
                                              type="radio" 
                                              checked={isOptionActive}
                                              onChange={() => {
                                                // Symmetrical: Toggle the active previewed option index instantly!
                                                onUpdateComponentWrap(selectedComponent.id, { 
                                                  activeOptionIndex: idx,
                                                  subbedGlbUrl: opt.glbUrl // Fallback compatibility
                                                });
                                              }}
                                              className="accent-indigo-500 h-3 w-3 cursor-pointer shrink-0"
                                              title="Set as active previewed variant"
                                            />
                                            <span className="font-extrabold uppercase text-[10px] truncate">
                                              {opt.name || `Variant ${idx + 1}`}
                                            </span>
                                          </div>
                                          
                                          <button
                                            type="button"
                                            onClick={() => {
                                              // Symmetrical: Remove the selected variant cleanly from array!
                                              const updatedOptions = selectedWrap.swapOptions.filter((_, oIdx) => oIdx !== idx);
                                              const newActiveIndex = Math.max(0, (selectedWrap.activeOptionIndex ?? 0) - 1);
                                              const activeUrl = updatedOptions[newActiveIndex]?.glbUrl || null;
                                              
                                              onUpdateComponentWrap(selectedComponent.id, {
                                                swapOptions: updatedOptions,
                                                activeOptionIndex: newActiveIndex,
                                                subbedGlbUrl: activeUrl
                                              });
                                            }}
                                            className="text-slate-500 hover:text-red-500 text-[10px] font-black uppercase shrink-0 h-6 w-6 flex items-center justify-center transition-colors"
                                            title="Delete this variant option"
                                          >
                                            ×
                                          </button>
                                        </div>
                                      );
                                    })}
                                  </div>
                                ) : (
                                  <div className="text-center py-2 bg-slate-950/20 border border-slate-900 rounded-lg p-2 mb-2">
                                    <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block">No custom options loaded yet.</span>
                                  </div>
                                )}

                                {/* Symmetrical Dropdown to add a new option from the shop's sellerComponents library */}
                                {sellerComponents.length > 0 && (
                                  <select
                                    value=""
                                    onChange={(e) => {
                                      const matched = sellerComponents.find(c => c.glbUrl === e.target.value);
                                      if (matched) {
                                        let rawUrl = matched.glbUrl;
                                        let fullUrl = rawUrl;
                                        if (rawUrl && !rawUrl.startsWith('http')) {
                                          const { data: storageUrl } = supabase.storage.from('component-images').getPublicUrl(rawUrl);
                                          fullUrl = storageUrl?.publicUrl || '';
                                        }

                                        const currentOptions = selectedWrap?.swapOptions || [];
                                        // Symmetrical Fallback: If subbedGlbUrl was defined but swapOptions was empty, migrate it!
                                        if (currentOptions.length === 0 && selectedWrap?.subbedGlbUrl) {
                                          currentOptions.push({
                                            glbUrl: selectedWrap.subbedGlbUrl,
                                            name: selectedWrap.componentName || 'Option 1'
                                          });
                                        }

                                        const newOption = {
                                          glbUrl: fullUrl,
                                          sellerComponentId: matched.id,
                                          name: matched.name
                                        };

                                        onUpdateComponentWrap(selectedComponent.id, { 
                                          subbedGlbUrl: fullUrl, // Fallback compatibility
                                          swapOptions: [...currentOptions, newOption],
                                          activeOptionIndex: currentOptions.length, // Preview newly added option immediately!
                                          position: selectedWrap?.position || [0, 0, 0],
                                          rotation: selectedWrap?.rotation || [0, 0, 0],
                                          scale: selectedWrap?.scale || [1, 1, 1]
                                        });
                                      }
                                    }}
                                    className="flex h-9 w-full rounded-md border border-slate-800 bg-slate-950 px-3 py-2 text-xs font-bold text-slate-400 uppercase tracking-wider outline-none focus-visible:ring-1 focus-visible:ring-indigo-500"
                                  >
                                    <option value="">-- Add variant option from library --</option>
                                    {sellerComponents.map(item => (
                                      <option key={item.id} value={item.glbUrl}>
                                        {item.name}
                                      </option>
                                    ))}
                                  </select>
                                )}

                                {/* Symmetrical Visual 3D Component Model (GLB) Drag-and-Drop Uploader */}
                                <div className="mt-2.5">
                                  <label className="flex flex-col items-center justify-center border border-dashed border-slate-700 bg-slate-950 hover:bg-slate-900 rounded-xl p-4 cursor-pointer transition-all gap-1.5 text-slate-400 group">
                                    <Icon name="Upload" size={18} className="text-indigo-400 group-hover:scale-110 transition-transform" />
                                    <span className="text-[10px] font-black uppercase tracking-wider text-slate-300">
                                      {uploadingGlbModel ? 'Uploading 3D Model...' : 'Upload custom GLB model'}
                                    </span>
                                    <input 
                                      type="file" 
                                      accept=".glb,.gltf" 
                                      onChange={handleGlbModelUpload} 
                                      disabled={uploadingGlbModel} 
                                      className="hidden" 
                                    />
                                  </label>
                                </div>
                              </div>
                              <p className="text-[9px] text-muted-foreground mt-1 leading-normal">
                                Paste a custom GLB/gltf URL or select a component from your shop's library. A visual transform gizmo will appear on the custom model once loaded!
                              </p>
                            </div>

                            {/* Symmetrical Clone Component button - Relocated just above color picker */}
                            {selectedWrap?.subbedGlbUrl && (
                              <div className="pt-1">
                                <button
                                  type="button"
                                  onClick={handleCloneComponent}
                                  className="w-full bg-indigo-600 hover:bg-indigo-500 text-white text-[10px] font-black uppercase tracking-wider h-9 rounded-lg flex items-center justify-center gap-1.5 cursor-pointer border-none shadow-sm shadow-indigo-600/10"
                                  title="Clone/duplicate this aftermarket component at an offset position!"
                                >
                                  <Icon name="Copy" size={12} />
                                  Clone Component
                                </button>
                              </div>
                            )}

                            {/* 3. Paint Color on Swapped/Base Component */}
                            {(selectedWrap?.subbedGlbUrl || mode === 'swap') && (
                              <div className="space-y-3">
                                <div>
                                  <label className="block text-[10px] font-bold uppercase text-slate-400 mb-1">
                                    {selectedWrap?.subbedGlbUrl ? 'Paint Swapped Component Color' : 'Paint Component Custom Color'}
                                  </label>
                                  <div className="flex items-center gap-3">
                                    <input 
                                      type="color" 
                                      value={selectedWrap?.color || '#ffffff'}
                                      onChange={(e) => onUpdateComponentWrap(selectedComponent.id, { color: e.target.value })}
                                      className="w-10 h-8 bg-transparent border-0 cursor-pointer rounded overflow-hidden"
                                    />
                                    <span className="text-xs font-mono text-slate-300">
                                      {selectedWrap?.color || '#ffffff'}
                                    </span>
                                    {selectedWrap?.color && (
                                      <button
                                        type="button"
                                        onClick={() => onUpdateComponentWrap(selectedComponent.id, { color: null })}
                                        className="text-[10px] font-black uppercase text-red-500 hover:text-red-400 ml-auto cursor-pointer"
                                      >
                                        Clear Color
                                      </button>
                                    )}
                                  </div>
                                </div>

                                {/* Symmetrical Add Swapped Component to Cart Button */}
                                {selectedWrap?.subbedGlbUrl && (
                                  <div className="pt-2 border-t border-slate-800/60 mt-2">
                                    <button
                                      type="button"
                                      onClick={() => {
                                        const activeOpt = selectedWrap?.swapOptions?.[selectedWrap.activeOptionIndex || 0] || {};
                                        const targetId = activeOpt.sellerComponentId || selectedWrap?.id;
                                        onAddComponentToCart({
                                          id: targetId,
                                          sellerComponentId: targetId,
                                          name: selectedWrap?.componentName || selectedComponent?.name || 'Swapped Part',
                                          price: selectedWrap?.price || 199.99
                                        });
                                      }}
                                      className="w-full bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-black uppercase tracking-wider h-10 rounded-xl flex items-center justify-center gap-1.5 cursor-pointer border-none shadow-md shadow-emerald-600/15"
                                      title="Add this custom aftermarket visual swap component to your shopping cart!"
                                    >
                                      <Icon name="ShoppingCart" size={13} />
                                      Add Swapped Part to Cart
                                    </button>
                                  </div>
                                )}
                              </div>
                            )}

                            {/* 4. Interactive Transform Gizmo Mode Toggles */}
                            {selectedWrap?.subbedGlbUrl && (
                              <div className="space-y-3">
                                <div>
                                  <label className="block text-[10px] font-bold uppercase text-slate-400 mb-1.5">
                                    Visual Transform Gizmo Mode
                                  </label>
                                  <div className="flex gap-1.5">
                                    {['translate', 'rotate', 'scale'].map(gMode => (
                                      <button
                                        key={gMode}
                                        type="button"
                                        onClick={() => onGizmoModeChange(gMode)}
                                        className={`flex-1 py-1.5 border rounded-lg text-[9px] font-black uppercase transition-all flex items-center justify-center gap-1 cursor-pointer ${
                                          gizmoMode === gMode 
                                            ? 'bg-indigo-600 border-indigo-700 text-white' 
                                            : 'bg-slate-950 border-slate-800 text-slate-400 hover:text-white'
                                        }`}
                                      >
                                        <Icon name={gMode === 'translate' ? "Move" : gMode === 'rotate' ? "RotateCcw" : "Maximize2"} size={10} />
                                        {gMode}
                                      </button>
                                    ))}
                                  </div>
                                </div>

                                {/* Symmetrical Gizmo Sizing Slider: Dynamically adjusts handle proportions to prevent off-frame closeup clipping! */}
                                <div className="border-b border-slate-800/60 pb-3">
                                  <div className="flex justify-between items-center mb-1">
                                    <label className="block text-[10px] font-bold uppercase text-slate-400">
                                      Gizmo Handle Size
                                    </label>
                                    <span className="text-[10px] font-black font-mono text-indigo-400">
                                      {gizmoSize.toFixed(1)}x
                                    </span>
                                  </div>
                                  <input 
                                    type="range"
                                    min="0.1"
                                    max="2.0"
                                    step="0.1"
                                    value={gizmoSize}
                                    onChange={(e) => onGizmoSizeChange(parseFloat(e.target.value))}
                                    className="w-full accent-indigo-600 h-1 bg-slate-800 rounded-lg appearance-none cursor-pointer mb-3"
                                  />
                                </div>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="space-y-4">
                            {/* Custom Friendly Name Input */}
                            <div>
                              <label className="block text-[10px] font-bold uppercase text-slate-400 mb-1">
                                Custom Panel Name
                              </label>
                              <Input 
                                type="text" 
                                value={selectedWrap?.componentName || selectedComponent.name || ''}
                                onChange={(e) => onUpdateComponentWrap(selectedComponent.id, { componentName: e.target.value })}
                                placeholder="e.g. Front Hood, Left Door"
                                className="bg-slate-950 border-slate-800 text-xs font-bold text-slate-200 uppercase tracking-wider"
                              />
                            </div>

                            {/* 1. Color Picker */}
                            <div>
                              <label className="block text-[10px] font-bold uppercase text-slate-400 mb-1">
                                Paint Wrap Color
                              </label>
                              <div className="flex items-center gap-3">
                                <input 
                                  type="color" 
                                  value={selectedWrap?.color || '#ffffff'}
                                  onChange={(e) => onUpdateComponentWrap(selectedComponent.id, { color: e.target.value })}
                                  className="w-10 h-8 bg-transparent border-0 cursor-pointer rounded overflow-hidden"
                                />
                                <span className="text-xs font-mono text-slate-300">
                                  {selectedWrap?.color || '#ffffff'}
                                </span>
                                {selectedWrap?.color && (
                                  <button
                                    type="button"
                                    onClick={() => onUpdateComponentWrap(selectedComponent.id, { color: null })}
                                    className="text-[10px] font-black uppercase text-red-500 hover:text-red-400 ml-auto cursor-pointer"
                                  >
                                    Clear Color
                                  </button>
                                )}
                              </div>
                            </div>

                            {/* 2. Roughness Slider */}
                            <div>
                              <div className="flex justify-between items-center mb-1">
                                <label className="text-[10px] font-bold uppercase text-slate-400">
                                  Satin Finish (Roughness)
                                </label>
                                <span className="text-[10px] font-bold text-slate-300">
                                  {selectedWrap?.roughness ?? 0.5}
                                </span>
                              </div>
                              <input 
                                type="range"
                                min="0"
                                max="1"
                                step="0.01"
                                value={selectedWrap?.roughness ?? 0.5}
                                onChange={(e) => onUpdateComponentWrap(selectedComponent.id, { roughness: parseFloat(e.target.value) })}
                                className="w-full accent-indigo-500 bg-slate-800 rounded-lg appearance-none h-1.5"
                              />
                            </div>

                            {/* 3. Metalness Slider */}
                            <div>
                              <div className="flex justify-between items-center mb-1">
                                <label className="text-[10px] font-bold uppercase text-slate-400">
                                  Metallic reflections
                                </label>
                                <span className="text-[10px] font-bold text-slate-300">
                                  {selectedWrap?.metalness ?? 0.5}
                                </span>
                              </div>
                              <input 
                                type="range"
                                min="0"
                                max="1"
                                step="0.01"
                                value={selectedWrap?.metalness ?? 0.5}
                                onChange={(e) => onUpdateComponentWrap(selectedComponent.id, { metalness: parseFloat(e.target.value) })}
                                className="w-full accent-indigo-500 bg-slate-800 rounded-lg appearance-none h-1.5"
                              />
                            </div>

                            {/* 4. Custom Logo / Decal Image Uploader */}
                            <div>
                              <label className="block text-[10px] font-bold uppercase text-slate-400 mb-1.5">
                                Apply Custom Graphic Decal
                              </label>
                              {selectedWrap?.decalUrl ? (
                                <div className="space-y-2">
                                  <div className="bg-slate-950 border border-slate-800 p-2 rounded-lg flex items-center justify-between gap-3">
                                    <img 
                                      src={selectedWrap.decalUrl} 
                                      alt="Decal Thumbnail"
                                      className="w-10 h-10 object-contain bg-white rounded border border-slate-800"
                                    />
                                    <button
                                      type="button"
                                      onClick={() => onUpdateComponentWrap(selectedComponent.id, { decalUrl: null })}
                                      className="text-[10px] font-black uppercase text-red-500 hover:text-red-400"
                                    >
                                      Remove Decal
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <label className="flex flex-col items-center justify-center border border-dashed border-slate-800 hover:border-slate-700 bg-slate-950/50 hover:bg-slate-950 p-4 rounded-xl cursor-pointer transition-colors group">
                                  <Icon name="Upload" size={18} className="text-slate-500 group-hover:text-indigo-400 transition-colors mb-1.5" />
                                  <span className="text-[10px] font-bold text-slate-400 group-hover:text-slate-300 uppercase tracking-wide">
                                    Upload PNG/JPG Decal
                                  </span>
                                  <input 
                                    type="file"
                                    accept="image/*"
                                    onChange={handleDecalUpload}
                                    className="hidden"
                                  />
                                </label>
                              )}
                            </div>

                            {/* Symmetrical Advanced Decal UV Transformations Slider */}
                            {selectedWrap?.decalUrl && (
                              <div className="bg-slate-950/30 border border-slate-800 rounded-xl p-3.5 space-y-3 mt-4 text-left animate-in zoom-in-95 duration-200">
                                <span className="text-[9px] font-black text-indigo-400 uppercase tracking-widest flex items-center gap-1.5 mb-1">
                                  <Icon name="Move" size={12} className="text-indigo-400 shrink-0" />
                                  Advanced Decal Transformations
                                </span>

                                {/* Decal Tiling Toggle Switch */}
                                <div className="flex items-center space-x-2 pb-1.5 border-b border-slate-800/40">
                                  <input 
                                    type="checkbox" 
                                    id="decalTiling"
                                    checked={!!selectedWrap.decalTiling} 
                                    onChange={(e) => onUpdateComponentWrap(selectedComponent.id, { decalTiling: e.target.checked })}
                                    className="rounded border-slate-800 text-indigo-600 focus:ring-indigo-500 h-3.5 w-3.5"
                                  />
                                  <label htmlFor="decalTiling" className="text-[10px] font-black uppercase text-slate-400 cursor-pointer select-none">
                                    Enable Decal Tiling (Repeat Pattern)
                                  </label>
                                </div>
                                
                                <div>
                                  <div className="flex justify-between items-center text-[10px] font-bold text-slate-400 mb-1">
                                    <span>Decal Size (Scale)</span>
                                    <div className="flex items-center gap-1.5">
                                      <input 
                                        type="number"
                                        step="0.1"
                                        min="0.1"
                                        max="10.0"
                                        value={selectedWrap.decalScaleX || 1.0}
                                        onChange={(e) => {
                                          const v = parseFloat(e.target.value) || 1.0;
                                          onUpdateComponentWrap(selectedComponent.id, { decalScaleX: v, decalScaleY: v });
                                        }}
                                        className="w-12 h-5 rounded border border-slate-800 bg-slate-950 text-center text-[9px] font-extrabold text-indigo-400"
                                      />
                                      <span className="text-indigo-400 font-extrabold">x</span>
                                    </div>
                                  </div>
                                  <input 
                                    type="range" 
                                    min="0.2" 
                                    max="5.0" 
                                    step="0.1" 
                                    value={selectedWrap.decalScaleX || 1.0}
                                    onChange={(e) => {
                                      const v = parseFloat(e.target.value);
                                      onUpdateComponentWrap(selectedComponent.id, { decalScaleX: v, decalScaleY: v });
                                    }}
                                    className="w-full accent-indigo-600 h-1 bg-slate-800 rounded-lg appearance-none cursor-pointer text-indigo-400"
                                  />
                                </div>

                                <div>
                                  <div className="flex justify-between items-center text-[10px] font-bold text-slate-400 mb-1">
                                    <span>Horizontal Shift (X)</span>
                                    <input 
                                      type="number"
                                      step="0.01"
                                      min="-10.0"
                                      max="10.0"
                                      value={selectedWrap.decalOffsetX || 0.0}
                                      onChange={(e) => {
                                        const v = parseFloat(e.target.value) || 0.0;
                                        onUpdateComponentWrap(selectedComponent.id, { decalOffsetX: v });
                                      }}
                                      className="w-12 h-5 rounded border border-slate-800 bg-slate-950 text-center text-[9px] font-extrabold text-indigo-400"
                                    />
                                  </div>
                                  <input 
                                    type="range" 
                                    min="-1.0" 
                                    max="1.0" 
                                    step="0.05" 
                                    value={selectedWrap.decalOffsetX || 0.0}
                                    onChange={(e) => {
                                      const v = parseFloat(e.target.value);
                                      onUpdateComponentWrap(selectedComponent.id, { decalOffsetX: v });
                                    }}
                                    className="w-full accent-indigo-600 h-1 bg-slate-800 rounded-lg appearance-none cursor-pointer"
                                  />
                                </div>

                                <div>
                                  <div className="flex justify-between items-center text-[10px] font-bold text-slate-400 mb-1">
                                    <span>Vertical Shift (Y)</span>
                                    <input 
                                      type="number"
                                      step="0.01"
                                      min="-10.0"
                                      max="10.0"
                                      value={selectedWrap.decalOffsetY || 0.0}
                                      onChange={(e) => {
                                        const v = parseFloat(e.target.value) || 0.0;
                                        onUpdateComponentWrap(selectedComponent.id, { decalOffsetY: v });
                                      }}
                                      className="w-12 h-5 rounded border border-slate-800 bg-slate-950 text-center text-[9px] font-extrabold text-indigo-400"
                                    />
                                  </div>
                                  <input 
                                    type="range" 
                                    min="-1.0" 
                                    max="1.0" 
                                    step="0.05" 
                                    value={selectedWrap.decalOffsetY || 0.0}
                                    onChange={(e) => {
                                      const v = parseFloat(e.target.value);
                                      onUpdateComponentWrap(selectedComponent.id, { decalOffsetY: v });
                                    }}
                                    className="w-full accent-indigo-600 h-1 bg-slate-800 rounded-lg appearance-none cursor-pointer"
                                  />
                                </div>

                                <div>
                                  <div className="flex justify-between items-center text-[10px] font-bold text-slate-400 mb-1">
                                    <span>Decal Angle (Rotation)</span>
                                    <div className="flex items-center gap-1.5">
                                      <input 
                                        type="number"
                                        step="1"
                                        min="-360"
                                        max="360"
                                        value={Math.round((selectedWrap.decalRotation || 0.0) * (180 / Math.PI))}
                                        onChange={(e) => {
                                          const deg = parseFloat(e.target.value) || 0;
                                          const rad = deg * (Math.PI / 180);
                                          onUpdateComponentWrap(selectedComponent.id, { decalRotation: rad });
                                        }}
                                        className="w-12 h-5 rounded border border-slate-800 bg-slate-950 text-center text-[9px] font-extrabold text-indigo-400"
                                      />
                                      <span className="text-[9px] text-slate-400">°</span>
                                    </div>
                                  </div>
                                  <input 
                                    type="range" 
                                    min="-3.14" 
                                    max="3.14" 
                                    step="0.05" 
                                    value={selectedWrap.decalRotation || 0.0}
                                    onChange={(e) => {
                                      const v = parseFloat(e.target.value);
                                      onUpdateComponentWrap(selectedComponent.id, { decalRotation: v });
                                    }}
                                    className="w-full accent-indigo-600 h-1 bg-slate-800 rounded-lg appearance-none cursor-pointer"
                                  />
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })() : (
                    <div className="space-y-4">
                      <div className="text-center py-4 bg-slate-950/40 border border-slate-800/40 rounded-xl p-3.5 shadow-inner">
                        <Icon name={mode === 'swap' ? 'RefreshCw' : 'Palette'} size={24} className="mx-auto text-indigo-500 mb-2.5 animate-pulse-soft" />
                        <p className="text-xs font-black uppercase tracking-wider text-slate-300">
                          {mode === 'swap' ? '3D Swap Studio Active' : 'Configure Wrap Layers'}
                        </p>
                        <p className="text-[10px] text-slate-400 mt-1.5 leading-relaxed max-w-[240px] mx-auto">
                          {mode === 'swap' 
                            ? 'Click directly on any vehicle component, or expand the Parts list at the bottom to swap base meshes with aftermarket .glb models, transform positions, or custom-paint on the fly.'
                            : 'Click on any vehicle component or panel inside the 3D canvas to begin custom wrapping, painting, or applying logos.'}
                        </p>
                        
                        {/* Symmetrical Add Accessory Button inside the Configuration Card */}
                        {mode === 'swap' && (
                          <div className="mt-4 pt-3 border-t border-slate-800/60 text-center space-y-3">
                            <button
                              type="button"
                              onClick={handleAddCustomAttachment}
                              className="bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-black uppercase tracking-wider h-10 px-5 rounded-xl flex items-center justify-center gap-1.5 mx-auto transition-all cursor-pointer border-none shadow-md shadow-indigo-600/15"
                              title="Add roof box, bike rack, spoiler, or custom accessory!"
                            >
                              <Icon name="Plus" size={13} />
                              Add Accessory
                            </button>

                            {/* Symmetrical Dropdown to spawn saved visual accessories */}
                            {sellerComponents.filter(c => c.glbUrl).length > 0 && (
                              <div className="max-w-[240px] mx-auto pt-1">
                                <select
                                  value=""
                                  onChange={(e) => {
                                    const matched = sellerComponents.find(c => c.id === e.target.value);
                                    if (matched) {
                                      const nextId = `attachment-${Date.now()}`;
                                      const nextName = matched.name;
                                      onUpdateComponentWrap(nextId, {
                                        hidden: false,
                                        componentName: nextName,
                                        subbedGlbUrl: matched.glbUrl,
                                        position: [0, 1.2, 0],
                                        rotation: [0, 0, 0],
                                        scale: [1, 1, 1],
                                        color: null,
                                        swapOptions: [{ glbUrl: matched.glbUrl, name: matched.name, sellerComponentId: matched.id }],
                                        activeOptionIndex: 0
                                      });
                                      onComponentSelect({ id: nextId, name: nextName });
                                    }
                                  }}
                                  className="flex h-9 w-full rounded-lg border border-slate-800 bg-slate-950 px-3 py-2 text-[10px] font-black uppercase text-indigo-400 tracking-wider outline-none focus-visible:ring-1 focus-visible:ring-indigo-500 cursor-pointer"
                                >
                                  <option value="">-- Or Spawn Saved Accessory --</option>
                                  {sellerComponents.filter(c => c.glbUrl).map(item => (
                                    <option key={item.id} value={item.id}>
                                      {item.name}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      {Object.keys(componentWraps).length > 0 && (
                        <div className="border-t border-slate-800/60 pt-3 text-left">
                          <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2">Customized Project Layers:</span>
                          <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
                            {Object.entries(componentWraps)
                              .filter(([compId, wrap]) => compId !== 'components' && compId !== 'product' && compId !== 'id' && compId !== 'shop_id' && compId !== 'assembly_id' && compId !== 'config_name' && typeof wrap === 'object' && wrap !== null && !Array.isArray(wrap))
                              .map(([compId, wrap]) => {
                              const compObj = components.find(c => c.id === compId || c.dbComponentId === compId);
                              const displayName = wrap.componentName || compObj?.name || compId;
                              
                              return (
                                <div key={compId} className="flex items-center gap-1.5 w-full">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      if (compObj) {
                                        onComponentSelect(compObj);
                                      } else {
                                        onComponentSelect({ id: compId, name: displayName });
                                      }
                                    }}
                                    className="flex-1 p-2.5 rounded-lg border border-slate-800 bg-slate-950 hover:bg-slate-900 text-slate-300 hover:text-white text-xs font-bold flex items-center justify-between transition-colors shadow-sm cursor-pointer text-left min-w-0"
                                  >
                                    <span className="truncate max-w-[65%]">• {displayName}</span>
                                    <div className="flex items-center gap-1.5 shrink-0">
                                      {wrap.color && (
                                        <span 
                                          className="w-3.5 h-3.5 rounded-full border border-slate-700 shadow-sm"
                                          style={{ backgroundColor: wrap.color }}
                                        />
                                      )}
                                      {wrap.decalUrl && (
                                        <span className="text-[8px] font-black uppercase bg-indigo-500/10 text-indigo-400 px-1 py-0.5 rounded">
                                          Decal
                                        </span>
                                      )}
                                    </div>
                                  </button>
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (!confirm(`Are you sure you want to remove the custom configurations for "${displayName}"?`)) return;
                                      onRemoveComponentWrap(compId);
                                    }}
                                    className="p-2.5 rounded-lg border border-slate-800 bg-slate-950 hover:bg-slate-900/60 text-slate-500 hover:text-red-500 transition-colors cursor-pointer shrink-0"
                                    title="Remove this customization layer"
                                  >
                                    <Icon name="Trash2" size={13} />
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Symmetrical Global Livery Save Form */}
                  <div className="border-t border-slate-800 pt-4 mt-2 space-y-3">
                    <div>
                      <label className="block text-[10px] font-bold uppercase text-slate-400 mb-1">
                        Project Name
                      </label>
                      <Input 
                        type="text"
                        value={wrapName}
                        onChange={(e) => setWrapName(e.target.value)}
                        placeholder="My Custom Design"
                        style={{ color: '#0a0d14', backgroundColor: '#ffffff', border: '1px solid #94a3b8' }} // Symmetrical: Explicit high-contrast inline styling to prevent light/dark white-on-white text blends!
                        className="text-xs font-black uppercase tracking-wider block w-full p-2.5 rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold uppercase text-slate-400 mb-1">
                        Project Notes
                      </label>
                      <textarea
                        value={wrapNotes}
                        onChange={(e) => setWrapNotes(e.target.value)}
                        placeholder="Add custom design notes, vinyl material specifications, or finish requests..."
                        style={{ color: '#0a0d14', backgroundColor: '#ffffff', border: '1px solid #94a3b8' }} // Symmetrical: Explicit high-contrast inline style!
                        className="flex min-h-[60px] w-full rounded-lg px-3 py-2 text-xs outline-none focus-visible:ring-1 focus-visible:ring-indigo-500"
                      />
                    </div>
                    <Button
                      type="button"
                      variant="default"
                      disabled={savingWrap}
                      onClick={handleSaveWrap}
                      className="bg-indigo-600 hover:bg-indigo-500 text-white font-black text-xs uppercase tracking-wider h-10 w-full"
                    >
                      {savingWrap ? 'Saving Project...' : mode === 'swap' ? 'Save Swap Project' : 'Save Wrap Design'}
                    </Button>
                  </div>
                </div>
              )}
              <div className="relative">
                <div className="flex justify-between items-start mb-2">
                  <h4 className="text-sm font-medium text-foreground">Description</h4>
                  {model?.isCertified && (
                    <img 
                      src="/assets/images/3dFactoryApprovedGearsWhite100x100.png" 
                      alt="Factory Certified" 
                      className="h-8 w-8 object-contain drop-shadow-sm"
                      title="Factory Certified"
                    />
                  )}
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {model?.description}
                </p>
                
                {/* Model Rating & Feedback */}
                <div className="mt-4 pt-4 border-t border-border/50">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-muted-foreground">Model Rating</span>
                    {renderStarRating(model?.averageRating, 14)}
                  </div>
                  <Link 
                    to={`/product-reviews-ratings?productId=${model?.id}`}
                    className="text-xs text-primary hover:underline flex items-center mb-3"
                  >
                    <Icon name="MessageSquare" size={12} className="mr-1" />
                    Give feedback about this model
                  </Link>

                  <div className="mt-3 pt-3 border-t border-border/50 flex justify-between items-center">
                    <span className="text-xs font-medium text-muted-foreground">Can't Find Part?</span>
                    <button 
                      onClick={onOpenUnspecifiedPartRequest}
                      className="text-xs text-primary hover:underline flex items-center font-semibold"
                    >
                      <Icon name="PlusCircle" size={12} className="mr-1 text-primary" />
                      Submit Request
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'specs' && (() => {
            const parsedSpecs = typeof model?.specs === 'string' ? (() => {
              try { return JSON.parse(model.specs); } catch { return {}; }
            })() : (model?.specs || {});
            
            const hasSpecs = Object.keys(parsedSpecs).length > 0;
            return (
              <div className="space-y-4 max-h-[300px] overflow-y-auto pr-2 text-left">
                 {!hasSpecs ? (
                   <div className="space-y-3">
                     <p className="text-sm text-muted-foreground italic mb-4">No custom specifications provided.</p>
                     <div className="grid grid-cols-2 gap-4">
                       <div>
                         <p className="text-xs text-muted-foreground">File Size</p>
                         <p className="text-sm font-medium text-foreground">{model?.fileSize || model?.file_size || 'N/A'}</p>
                       </div>
                       <div>
                         <p className="text-xs text-muted-foreground">Polygons</p>
                         <p className="text-sm font-medium text-foreground">{model?.polygons || 'N/A'}</p>
                       </div>
                     </div>
                     <div>
                       <p className="text-xs text-muted-foreground mb-2">Dimensions</p>
                       <p className="text-sm font-medium text-foreground">{model?.dimensions || 'N/A'}</p>
                     </div>
                   </div>
                 ) : (
                   <div className="space-y-4">
                      {Object.entries(parsedSpecs).map(([key, value]) => {
                        if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
                          return (
                            <div key={key} className="pt-2.5 pb-1 border-b border-slate-800/40 last:border-b-0 text-left">
                               <p className="text-xs font-black text-slate-400 uppercase tracking-wider mb-2 border-b border-slate-800/60 pb-1">{key.replace(/_/g, ' ')}</p>
                               <div className="mt-1">
                                 {renderSpecValue(value)}
                               </div>
                            </div>
                          );
                        }
                        return (
                          <div key={key} className="flex justify-between items-center py-2 border-b border-slate-800/40 last:border-b-0 text-left">
                             <p className="text-xs text-slate-400 capitalize font-bold">{key.replace(/_/g, ' ')}</p>
                             <p className="text-xs font-medium text-foreground text-right">{renderSpecValue(value)}</p>
                          </div>
                        );
                      })}
                   </div>
                 )}
              </div>
            );
          })()}

          {activeTab === 'swaps' && (
            <div className="p-4 space-y-3.5 text-left max-h-[300px] overflow-y-auto pr-2">
              <span className="text-[10px] font-black text-indigo-400 uppercase tracking-widest block mb-1">
                Shop Custom Swaps
              </span>
              <p className="text-[10px] text-muted-foreground leading-normal">
                Click on any of the aftermarket upgrade packages below to instantly swap out and preview the custom configurations on the model in real-time!
              </p>
              
              <div className="space-y-2">
                {storeSwapConfigs.map(config => {
                  const isApplied = componentWraps && Object.keys(componentWraps).length > 0 && 
                                    (componentWraps === config.config_data?.after || 
                                     JSON.stringify(componentWraps) === JSON.stringify(config.config_data?.after));
                                     
                  return (
                    <button
                      key={config.id}
                      type="button"
                      onClick={() => {
                        if (isApplied) {
                          // Toggle off - reset back to standard
                          onUpdateComponentWrap('_reset', null); // Revert all swaps
                        } else {
                          // Apply this swap config's visual layers directly to canvas!
                          const subbedLayers = config.config_data?.after || {};
                          onUpdateComponentWrap('_apply_bulk', subbedLayers);
                        }
                      }}
                      className={`w-full p-3 rounded-xl border text-left flex flex-col gap-1.5 transition-colors cursor-pointer ${
                        isApplied 
                          ? 'border-indigo-500 bg-indigo-500/10 text-indigo-700 font-extrabold dark:text-indigo-400 shadow-sm' 
                          : 'border-border hover:border-border/80 bg-muted/20 text-foreground'
                      }`}
                    >
                      <div className="flex justify-between items-center w-full">
                        <span className="text-xs font-black uppercase tracking-wider">{config.config_name}</span>
                        <Icon name={isApplied ? "CheckCircle" : "ChevronRight"} size={14} className={isApplied ? "text-indigo-600" : "text-slate-400"} />
                      </div>
                      
                      {config.config_data?.notes && (
                        <p className="text-[10px] text-muted-foreground line-clamp-2 leading-relaxed">
                          {config.config_data.notes}
                        </p>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          </div>
        </div>

        {/* Collapsible Components Drawer at the bottom - No header button anymore */}
        {isComponentsExpanded && (
          <div className="flex flex-col border-t border-border bg-muted/30 animate-in slide-in-from-bottom-2 duration-200">
            {/* Component Search & Actions */}
            <div className="p-2 bg-card/50 flex items-center gap-2">
              <div className="relative flex-1">
                <input
                  type="text"
                  placeholder="Search parts..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="flex h-8 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 text-xs pr-8"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    <Icon name="X" size={14} />
                  </button>
                )}
              </div>
            </div>

            {/* Component List */}
            <div className="overflow-y-auto overflow-x-hidden max-h-[500px] flex-1 p-1 space-y-1">
              {Object.entries(groupedComponents || {})
                .sort(([a], [b]) => {
                  if (a === 'Hidden / Custom Swapped Parts') return -1;
                  if (b === 'Hidden / Custom Swapped Parts') return 1;
                  return a.localeCompare(b);
                })
                .map(([groupName, groupComponents]) => {
                  // Symmetrical Auto-Expand on Search:
                  // Force-expands the group folder when actively searching, immediately showing matching items!
                  const isGroupExpanded = expandedGroups.has(groupName) || !!searchTerm;
                  
                  return (
                    <div key={groupName} className="rounded-md overflow-hidden">
                      <button
                        onClick={() => toggleGroup(groupName)}
                        className="w-full flex items-center justify-between p-2 text-left bg-muted/20 hover:bg-muted/40"
                      >
                        <div className="flex items-center space-x-2 min-w-0">
                          <Icon name={isGroupExpanded ? "ChevronDown" : "ChevronRight"} size={14} />
                          <span className="text-xs font-semibold text-foreground uppercase tracking-wider truncate">{groupName}</span>
                        </div>
                      </button>

                      {isGroupExpanded && groupComponents && (
                        <div className="space-y-0.5 mt-0.5">
                          {groupComponents.map((component) => component && component.id ? (
                            <div key={component.id} id={`panel-comp-${component.id}`} className="flex flex-col mb-1">
                              <div
                                className={`flex items-center p-2 rounded-md transition-all ${
                                  selectedComponent?.id === component.id ? 'bg-primary/20 ring-1 ring-primary/30' : 'hover:bg-muted/30'
                                }`}
                                onDoubleClick={(e) => {
                                  e.stopPropagation();
                                  if (onComponentDoubleClick) {
                                    onComponentDoubleClick(component);
                                  }
                                }}
                                onContextMenu={(e) => {
                                  if (mode !== 'wrap') return; // Only allow custom right click in Wrap mode!
                                  e.preventDefault();
                                  if (onComponentDoubleClick) {
                                    onComponentDoubleClick(component);
                                  }
                                }}
                              >
                          <div className="flex items-center space-x-2 flex-1 min-w-0">
                            {/* Isolate Button */}
                            <button
                              onClick={(e) => handleIsolateComponent(component.id, e)}
                              className={`flex-shrink-0 transition-colors ${
                                isolatedComponentId === component.id 
                                  ? 'text-accent' 
                                  : 'text-muted-foreground hover:text-foreground'
                              }`}
                              title={isolatedComponentId === component.id ? "Exit Isolation" : "Isolate this component"}
                            >
                              <Icon name="Focus" size={14} className={isolatedComponentId === component.id ? "fill-current" : ""} />
                            </button>
                            
                            {/* Visibility Toggle */}
                            <button onClick={(e) => handleToggleVisibility(component.id, e)} className="flex-shrink-0" title={isComponentVisible(component.id) ? "Hide component" : "Show component"}>
                              <Icon 
                                name={
                                  (mode === 'swap' || mode === 'wrap')
                                    ? (componentWraps[component.id]?.hidden || componentWraps[component.name]?.hidden) ? "EyeOff" : "Eye"
                                    : isComponentVisible(component.id) ? "Eye" : "EyeOff"
                                } 
                                size={14} 
                                className={
                                  (mode === 'swap' || mode === 'wrap')
                                    ? (componentWraps[component.id]?.hidden || componentWraps[component.name]?.hidden) ? 'text-muted-foreground' : 'text-primary'
                                    : isComponentVisible(component.id) ? 'text-primary' : 'text-muted-foreground'
                                }
                              />
                            </button>

                            {/* Z-Ray Vision Toggle */}
                            {(() => {
                              const isZRay = zRayComponentIds && (
                                zRayComponentIds.includes(component.id) ||
                                (component.name && zRayComponentIds.includes(component.name)) ||
                                (component.dbComponentId && zRayComponentIds.includes(component.dbComponentId)) ||
                                (cleanMeshName(component.name) && zRayComponentIds.includes(cleanMeshName(component.name)))
                              );
                              return (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (onToggleZRay) {
                                      onToggleZRay(component);
                                    }
                                  }}
                                  className={`flex-shrink-0 transition-colors p-0.5 rounded hover:bg-muted/40 ${
                                    isZRay
                                      ? 'text-cyan-500 font-bold'
                                      : 'text-muted-foreground hover:text-foreground'
                                  }`}
                                  title={isZRay ? "Turn off Z-Ray for this component" : "Render this component in Z-Ray"}
                                >
                                  <Icon 
                                    name="Scan" 
                                    size={14} 
                                    className={isZRay ? "text-cyan-500" : ""} 
                                  />
                                </button>
                              );
                            })()}
                            <button 
                              onClick={() => onComponentSelect(component)}
                              className="flex-1 text-left break-words whitespace-normal flex items-center gap-2"
                            >
                              <span className={`text-xs ${selectedComponent?.id === component.id ? 'font-bold text-primary' : 'text-foreground'}`}>
                                {component.name}
                              </span>
                              {(component.is3dManufacturing || component.isCncCapable) && (
                                <span className="px-1 py-0 bg-orange-600 text-white text-[8px] font-black rounded shadow-sm">3D</span>
                              )}
                            </button>
                          </div>
                        </div>
                        
                        {/* Sellers list underneath */}
                        {(() => {
                          const options = (component.availableOptions || []).filter(o => (o.price > 0) || o.is3dManufacturing || o.isCncCapable);
                          if (options.length === 0 && (component.price || component.is3dManufacturing || component.isCncCapable)) {
                              // If no specific options but base component is capable/priced, use it
                              if (!options.some(o => o.id === component.id)) options.push(component);
                          }
                          
                          if (options.length > 0) {
                            return (
                              <div className="pl-9 pr-2 pb-1 space-y-1">
                                {options.map(option => (
                                  <button 
                                    key={option.id || option.dbComponentId} 
                                    onClick={(e) => { e.stopPropagation(); onAddComponentToCart(option); }}
                                    onContextMenu={(e) => { 
                                      if (mode !== 'wrap') return; // Only allow custom right click in Wrap mode!
                                      e.preventDefault(); 
                                      e.stopPropagation(); 
                                      onAddComponentToCart(option); 
                                    }}
                                    className="w-full flex items-center justify-between text-[10px] hover:bg-primary/10 p-1.5 rounded transition-colors"
                                  >
                                    <span className="flex-1 min-w-0 text-muted-foreground break-words whitespace-normal mr-2 text-left flex items-center flex-wrap gap-y-1" title={option.customName || option.name}>
                                      <Icon name="ShoppingCart" size={12} className="text-primary mr-1.5 flex-shrink-0" />
                                      <span className="truncate max-w-[150px] md:max-w-[200px]" title={option.customName || option.name}>
                                        {option.customName || option.name} 
                                      </span>
                                      {option.sellerBusinessName && <span className="opacity-70 ml-1 truncate max-w-[100px]">(by {option.sellerBusinessName})</span>}
                                      {(option.is3dManufacturing || option.isCncCapable) && (
                                        <span className="ml-2 px-1 py-0 bg-orange-600 text-white text-[8px] font-black rounded shadow-sm">3D</span>
                                      )}
                                    </span>
                                    <span className="font-bold text-primary flex-shrink-0">{formatPrice(option.price)}</span>
                                  </button>
                                ))}
                              </div>
                            );
                          }

                          if (model?.sell_product_only) {
                            return (
                              <div className="pl-9 pr-2 pb-2 text-left">
                                <span className="text-[10px] text-muted-foreground/80 italic font-medium">Only available with entire product</span>
                              </div>
                            );
                          }

                          return null;
                        })()}
                      </div>
                    ) : null)}
                  </div>
                )}
              </div>
            );
          })}
          </div>
        </div>
      )}
    </div>
  );
};

export default ModelInfoPanel;
